import type { ReactNode } from 'react'
import { Alert, Badge, Group, Stack, Text } from '@mantine/core'
import {
  IconAlertCircle, IconAlertTriangle, IconArchive, IconInfoCircle, IconPlugConnectedX, IconSearch,
} from '@tabler/icons-react'
import { EmptyState, MetaList, SectionCard, Skeleton } from '@lighthouse/design'
import { RestrictedState } from '../access/RestrictedState'
import { CartographerLink } from '../cartographer/CartographerLink'
import { useReportDetailOverview } from '../../hooks/useReportDetail'
import { NOT_MEASURED, exactNumber, whenLabel } from '../../utils/format'
import type { ReportDetailOverview, ReportDetailWindow } from '../../types/reportDetail'
import {
  LAST_NON_SCHEDULED_LABEL,
  REPORT_DETAIL_SURFACE,
  archivedBannerText,
  grantsSubtitle,
  groupOrRoleGrantsText,
  lastExecutionLabel,
  notRecordedLabel,
  schedulesSummaryText,
  ownerText,
  principalText,
  tabErrorView,
  userGrantsText,
  windowTitle,
  workspaceText,
} from './reportDetailFormat'

// ===========================================================================
// Atoms every Report Detail tab shares. They live beside the first tab because
// the tabs are this folder's only components; each tab imports them from here.
// ===========================================================================

/** A tab whose read is in flight (or in the retry gap): a skeleton, never an empty tab. */
export function TabLoading() {
  return (
    <Stack gap="sm" aria-busy="true" data-state="loading">
      <Skeleton height={64} />
      <Skeleton height={160} />
    </Stack>
  )
}

/**
 * A tab whose read failed, by status (reportDetailFormat.tabErrorView): 403 is
 * restricted and neutral, 404 is "No such report", 400 is the window, and a 5xx
 * names the migration the read depends on — or says it depends on none.
 */
export function TabFailure({ error, what, migration }: { error: unknown; what: string; migration?: string }) {
  const view = tabErrorView(error, what, migration)
  if (view.kind === 'restricted') {
    return <RestrictedState surface={REPORT_DETAIL_SURFACE} reason={view.reason} />
  }
  const icon = view.kind === 'missing' ? <IconSearch size={28} stroke={1.5} />
    : view.kind === 'unreachable' ? <IconPlugConnectedX size={28} stroke={1.5} />
      : <IconAlertCircle size={28} stroke={1.5} />
  return (
    <div role="status" data-state={`error-${view.kind}`}>
      <EmptyState icon={icon} title={view.title} message={view.message} />
    </div>
  )
}

/** A dimmed caption: the served sentence that qualifies a figure. */
export function Caption({ children }: { children: ReactNode }) {
  return <Text size="xs" c="dimmed">{children}</Text>
}

/** A figure, with its reason beside it when it is not measured. */
export function Figure({ value, reason }: { value: ReactNode; reason?: string | null }) {
  return (
    <Group gap={6} wrap="wrap" align="baseline">
      <Text size="sm" fw={500}>{value}</Text>
      {reason && <Text size="xs" c="dimmed">{reason}</Text>}
    </Group>
  )
}

/** When the server evaluated the response. */
export function EvaluatedAt({ at }: { at: string }) {
  return <Caption>Evaluated {whenLabel(at)}.</Caption>
}

/**
 * The run-log window every run figure sits under: its title from what the log
 * MEASURABLY covers ("Last 30 days", "Since … UTC", or NOT_MEASURED for an
 * empty log), and the server's caption, which names both where the figures
 * start and the retention that bounds them (90 days by default).
 */
export function WindowBanner({ window }: { window: ReportDetailWindow }) {
  return (
    <Alert
      variant="light"
      color={window.coverage === 'Complete' ? 'gray' : 'yellow'}
      icon={<IconInfoCircle size={16} />}
      title={windowTitle(window)}
      data-coverage={window.coverage}
    >
      <Text size="sm">{window.caption}</Text>
    </Alert>
  )
}

// ===========================================================================
// Overview
// ===========================================================================

/** Report Detail → Overview: the report, where it lives, whose it is, and what reaches it. */
export function OverviewTab({ reportId }: { reportId: string }) {
  const { data, isLoading, isError, error } = useReportDetailOverview(reportId, { open: true })

  if (isError) return <TabFailure error={error} what="this report" />
  if (isLoading || !data) return <TabLoading />

  return <OverviewBody overview={data} />
}

function OverviewBody({ overview }: { overview: ReportDetailOverview }) {
  const archived = archivedBannerText(overview)
  const { report } = overview

  return (
    <Stack gap="lg">
      {archived && (
        <Alert variant="light" color="gray" icon={<IconArchive size={16} />} title="Archived" data-state="archived">
          <Stack gap={4}>
            <Text size="sm">{archived}.</Text>
            <CartographerLink path="/archive" label="Archive">Cartographer’s archive</CartographerLink>
          </Stack>
        </Alert>
      )}

      {overview.attentionRequired && (
        <Alert variant="light" color="yellow" icon={<IconAlertTriangle size={16} />} title="Attention required">
          <Stack gap={4}>
            <Text size="sm">
              {overview.attentionSince
                ? `Flagged by the orphan check, detected ${whenLabel(overview.attentionSince)}.`
                : 'Flagged by the orphan check; no detection time is stored.'}{' '}
              The Governance tab shows the stored reason; Cartographer is where ownership is taken up.
            </Text>
            <CartographerLink path="/governance" label="Governance">Cartographer’s Governance page</CartographerLink>
          </Stack>
        </Alert>
      )}

      <SectionCard title="Report">
        <MetaList
          items={[
            { label: 'Status', value: <Badge variant="light" color="gray">{report.status}</Badge> },
            {
              label: 'Runnable',
              value: report.isRunnable ? 'Yes' : 'No — view-only: nobody can run, export or schedule it',
            },
            { label: 'Description', value: report.description?.trim() ? report.description : NOT_MEASURED },
            { label: 'Filed in', value: workspaceText(overview) },
            { label: 'Owner', value: ownerText(overview.owner) },
            { label: 'Maintainer', value: principalText(overview.maintainerPrincipalId, overview.maintainerPrincipalType) },
            {
              label: 'Creator (stamped at creation)',
              value: overview.creatorSnapshot ?? `${NOT_MEASURED} not stamped`,
            },
            { label: 'Created', value: whenLabel(report.createdAt) },
            { label: 'Modified', value: whenLabel(report.modifiedAt) },
            { label: 'Report id', value: report.id, mono: true },
          ]}
        />
      </SectionCard>

      <SectionCard title="Executions" subtitle="From the report row and the run log; neither records a view">
        <Stack gap="md">
          <Stack gap={2} data-figure="last-execution">
            <Text size="xs" c="dimmed" tt="uppercase">{lastExecutionLabel(overview.interactiveRecordable)}</Text>
            <Figure
              value={whenLabel(overview.lastExecutionAt)}
              reason={overview.lastExecutionAt == null ? 'no execution has been recorded' : null}
            />
            <Caption>{overview.lastExecutionCaption}</Caption>
          </Stack>
          <Stack gap={2} data-figure="last-non-scheduled">
            <Text size="xs" c="dimmed" tt="uppercase">{LAST_NON_SCHEDULED_LABEL}</Text>
            <Figure
              value={whenLabel(overview.lastNonScheduledRunAt)}
              reason={overview.lastNonScheduledRunAt == null ? overview.lastNonScheduledRunUnavailableReason : null}
            />
          </Stack>
        </Stack>
      </SectionCard>

      <SectionCard title="What reaches it" subtitle={grantsSubtitle(report)}>
        <MetaList
          items={[
            { label: 'Grants to single users', value: userGrantsText(overview.liveUserGrantCount) },
            {
              label: 'Grants to groups or roles',
              value: (
                <Stack gap={2}>
                  <Text size="sm">{groupOrRoleGrantsText(overview.liveGroupOrRoleGrantCount)}</Text>
                  <Caption>{overview.liveGroupOrRoleGrantCaption}</Caption>
                </Stack>
              ),
            },
            {
              label: 'Visibility (stored)',
              value: (
                <Stack gap={2}>
                  <Text size="sm">{overview.visibility.value}{overview.visibility.isEnforced ? '' : ' — not enforced'}</Text>
                  <Caption>{overview.visibility.caption}</Caption>
                </Stack>
              ),
            },
            {
              label: 'Schedules',
              value: schedulesSummaryText(
                overview.activeScheduleCount, overview.pausedScheduleCount, overview.deliveringScheduleCount),
            },
            { label: 'Favourites', value: `${exactNumber(overview.favouriteCount)} — bookmarks, not grants` },
          ]}
        />
      </SectionCard>

      <SectionCard title="Not recorded" subtitle="Reserved by the schema and written by nothing in v1">
        <MetaList
          items={overview.notRecorded.map((row) => ({
            label: notRecordedLabel(row.field),
            value: <Figure value={NOT_MEASURED} reason={row.reason} />,
          }))}
        />
      </SectionCard>

      <EvaluatedAt at={overview.evaluatedAt} />
    </Stack>
  )
}
